/**
 * The section filter bar above the feed (NEWS-297; the feature is NEWS-97,
 * [22 — Topic categories](../../docs/22-topic-categories.md) FR-22.9–22.12;
 * kerf UI, NM-MACR4Q).
 *
 * It filters the **feed** by taxonomy section. Two kerf UI `ChoiceChips`
 * groups — sections, then the selected section's subsections — each an
 * exclusive radio group: one Tab stop, arrow keys select, and the checked
 * value is the filter. "All" is a sentinel value rather than an empty string,
 * which a radio cannot meaningfully carry.
 *
 * **The sub-row's container is always in the DOM**, empty when no section is
 * selected, so the bar's structure never changes as you select (docs/3-ui.md)
 * and its height doesn't jump. Rendering only: the groups' `change` events are
 * handled by `delegate()` in `app.tsx`.
 */

import { ChoiceChips } from '@kerfjs/ui/choice-chips';
import { List } from '@kerfjs/ui/list';
import type { SafeHtml } from 'kerfjs';

import type { PulseResp } from '../../api/schemas.js';
import {
  BUILTIN_CATEGORIES,
  findCategory,
  hasUncategorized,
  NO_SUBCATEGORY_FILTER,
  UNCATEGORIZED_FILTER,
  UNCATEGORIZED_LABEL,
  visibleCategories,
  visibleSubcategories,
} from '../../categories.js';
import type { Topic } from '../../db/schemas.js';
import type { AppState } from '../app/stores.js';
import { categoryPulseJsx } from '../features/pulse/pulse-view.js';

/**
 * The section filter bar (NEWS-97) — a newspaper's section navigation.
 *
 * Two rows, and the second is deliberately styled unlike the first: the top row
 * is the masthead's sections, the sub-row is that section's subsections. Same
 * shape a newspaper uses, and it keeps "which level am I on" legible without a
 * label saying so.
 *
 * The sub-row only appears once a category is selected — every section plus
 * every one of their subcategories in one bar would be a wall rather than
 * navigation.
 */
/** The "All" choice of either group — every section, or every subsection. */
export const ALL_FILTER = '__all__';

export function filterBarJsx(
  selected: AppState['categoryFilter'],
  topics: readonly Topic[],
  pulse: PulseResp | null = null,
  soloActive = false,
): SafeHtml {
  // Nothing to filter (NM-274, FR-22.19): no topics and no selection renders no
  // bar at all — a lone "All" filtered nothing.
  if (topics.length === 0 && selected === null) return <></>;
  const table = visibleCategories(BUILTIN_CATEGORIES, topics, selected?.category ?? null);
  const current = selected === null ? null : findCategory(table, selected.category);
  const subs =
    current === undefined || current === null
      ? []
      : visibleSubcategories(BUILTIN_CATEGORIES, current.slug, topics, selected?.subcategory ?? null);
  const sections = [
    { value: ALL_FILTER, label: 'All' },
    ...table.map((category) => ({ value: category.slug, label: category.label })),
    // Selects the absence of a category, which no table row can express — hence
    // a sentinel slug. Shown only when something is unclassified (NEWS-114).
    ...(hasUncategorized(topics, selected?.category ?? null)
      ? [{ value: UNCATEGORIZED_FILTER, label: UNCATEGORIZED_LABEL }]
      : []),
  ];
  return (
    <nav aria-label="Filter by Section" aria-disabled={soloActive ? 'true' : undefined} data-filter-bar="">
      <List gap="2xs">
        {/* The chips take the control inset; the section pulse below is a kerf
            UI ContentItem, which owns its own inline margin (NM-NWFSDV). */}
        <List gap="2xs" controlInsets="rl">
          <ChoiceChips
            name="filter-category"
            label="Section"
            action="filter-category"
            size="compact"
            disabled={soloActive}
            value={selected?.category ?? ALL_FILTER}
            choices={sections}
          />
          <div data-filter-subsections="">
            {soloActive || subs.length === 0 ? (
              ''
            ) : (
              <ChoiceChips
                name="filter-subcategory"
                label={`${current?.label ?? 'Section'} Subsections`}
                action="filter-subcategory"
                size="compact"
                value={selected?.subcategory ?? ALL_FILTER}
                choices={[
                  { value: ALL_FILTER, label: `All ${current?.label ?? ''}` },
                  ...subs.map((sub) => ({ value: sub.slug ?? NO_SUBCATEGORY_FILTER, label: sub.label })),
                ]}
              />
            )}
          </div>
        </List>
        {/* The section's pulse summary (NEWS-453), when a section is selected. */}
        <div data-category-pulse="">
          {!soloActive && selected !== null && pulse !== null ? categoryPulseJsx(pulse) : ''}
        </div>
      </List>
    </nav>
  );
}
